import { useState } from 'react';
import {
  Box, IconButton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Icon from '@mdi/react';
import { mdiCalendarRemove, mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';

const SHUTDOWN_DATE = new Date(2027, 0, 1);
const DISMISSED_KEY = 'mgm_shutdown_banner_dismissed';
const ACCENT = '#B45309';

const COPY = {
  en: {
    title: 'ManageMate is closing on January 1, 2027',
    description: 'After this date the app will no longer be available. '
      + 'Please export any schedules, reports and team data you want to keep before then. Thank you for being with us.',
    daysLeft: (days: number) => (days === 1 ? '1 day left' : `${days} days left`),
    close: 'Dismiss for today',
  },
  es: {
    title: 'ManageMate cerrará el 1 de enero de 2027',
    description: 'A partir de esa fecha la aplicación dejará de estar disponible. '
      + 'Exporta los horarios, reportes y datos del equipo que quieras conservar antes de esa fecha. Gracias por acompañarnos.',
    daysLeft: (days: number) => (days === 1 ? 'Queda 1 día' : `Quedan ${days} días`),
    close: 'Ocultar por hoy',
  },
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const getDaysLeft = () => Math.max(0, Math.ceil((SHUTDOWN_DATE.getTime() - Date.now()) / 86400000));

const ShutdownBanner = () => {
  const { i18n } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === todayKey());

  if (isDismissed) return null;

  const copy = i18n.language?.startsWith('es') ? COPY.es : COPY.en;
  const daysLeft = getDaysLeft();

  const onDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, todayKey());
    setIsDismissed(true);
  };

  return (
    <Box
      role="status"
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        borderRadius: 2,
        border: `1px solid ${alpha(ACCENT, 0.24)}`,
        borderLeft: `4px solid ${ACCENT}`,
        backgroundColor: '#FFFBEB',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: ACCENT,
          backgroundColor: alpha(ACCENT, 0.12),
        }}
      >
        <Icon path={mdiCalendarRemove} size={1} />
      </Box>

      <Stack spacing={0.5} flexGrow={1} minWidth={0}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Typography variant="subtitle1" sx={{ color: '#78350F', fontWeight: 700 }}>
            {copy.title}
          </Typography>
          {daysLeft > 0 && (
            <Box
              component="span"
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                color: '#FFFFFF',
                backgroundColor: ACCENT,
              }}
            >
              {copy.daysLeft(daysLeft)}
            </Box>
          )}
        </Stack>
        <Typography variant="body2" sx={{ color: '#92400E' }}>
          {copy.description}
        </Typography>
      </Stack>

      <IconButton
        aria-label={copy.close}
        title={copy.close}
        onClick={onDismiss}
        size="small"
        sx={{ flexShrink: 0, color: '#92400E' }}
      >
        <Icon path={mdiClose} size={0.9} />
      </IconButton>
    </Box>
  );
};

export {
  ShutdownBanner,
};
